'use client';
import React from 'react';

import Bookmaker from '@src/components/Icons/Bookmaker';

import CardMetric from '@src/components/Shared/CardMetric';
import FractionDisplay from '@src/components/Shared/FractionDisplay';
import NextLink from '@src/components/Shared/NextLink';

import useIsMobile from '@src/hooks/useIsMobile';
import useDebounce from '@src/hooks/useDebounce';

import ForceInput from '../ForceInput/ForceInput';
import UnitDropdown from '../UnitDropdown/UnitDropdown';
import { UnitTypes } from '../UnitDropdown/UnitDropdown.types';
import { JointRatioCardPropTypes } from './JointRatioCard.types';

const units: UnitTypes[] = [
  { id: 'newtons', name: 'Newtons', unit: 'N' },
  { id: 'pounds', name: 'Pounds', unit: 'lb' },
  { id: 'kilograms', name: 'Kilograms', unit: 'kg' },
];

const JointRatioCard: React.FC<JointRatioCardPropTypes> = ({}) => {
  const isMobile = useIsMobile();

  // Main values for calculation
  const [agonistValue, setAgonistValue] = React.useState('');
  const [antagonistValue, setAntagonistValue] = React.useState('');

  // Temporary values for mobile
  const [tempAgonistValue, setTempAgonistValue] = React.useState('');
  const [tempAntagonistValue, setTempAntagonistValue] = React.useState('');

  const [unit, setUnit] = React.useState<string>(units[0].id);

  const handleCalculate = () => {
    setAgonistValue(tempAgonistValue);
    setAntagonistValue(tempAntagonistValue);
  };

  const convertToNewtons = (value: string, fromUnit: string): number => {
    const v = parseFloat(value);
    if (isNaN(v)) return 0;
    switch (fromUnit) {
      case 'pounds':
        return v / 0.224809;
      case 'kilograms':
        return v / 0.1019716;
      default:
        return v; // newtons
    }
  };

  // Always use main values for calculation
  const agonistN = convertToNewtons(agonistValue, unit);
  const antagonistN = convertToNewtons(antagonistValue, unit);

  let ratio = 0;
  if (antagonistN !== 0) ratio = agonistN / antagonistN;
  const formattedRatio = ratio.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // --- Debounce the formattedRatio by 3000 ms ---
  const debouncedRatio = useDebounce(formattedRatio, 3000);

  // Fire a tracking event whenever a valid debounced joint ratio is computed
  React.useEffect(() => {
    if (
      agonistValue !== '' &&
      antagonistValue !== '' &&
      !isNaN(Number(debouncedRatio)) &&
      isFinite(Number(debouncedRatio))
    ) {
      if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: 'msk_calculator_calculation_performed',
          eventCategory: 'msk_calculator_usage',
          eventLabel: 'msk_calculator_joint_ratio',
          value: 1,
        });
      }
    }
    // Only re-run when debouncedRatio changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedRatio]);

  // Check if mobile has pending changes
  const hasPendingChanges =
    isMobile && (tempAgonistValue !== agonistValue || tempAntagonistValue !== antagonistValue);

  return (
    <CardMetric
      id='calculator-joint-ratio'
      title='Joint Ratio'
      productIcons={[
        {
          alt: 'ForceFrame App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/7dxABhvlBmXLF0UuYpAdss/883fc648f9bc2f93d9d05d9095800e67/forceframe-app-icon.svg',
        },
        {
          alt: 'Dynamo App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/5KQtYsCw5caSZvIuohrRmm/c174bb8572e3170009776e40c21f18e9/dynamo-app-icon.svg',
        },
      ]}
    >
      <p className='mb-[2.2rem] text-[1.2rem]'>
        Assess the balance between agonist and antagonist muscle strength, such as shoulder ER/IR,
        hamstrings&nbsp;:&nbsp;quadriceps, and hip AD/AB ratios.
      </p>

      <div className='mb-[1.6rem] flex flex-col md:mb-[unset] md:flex-row'>
        <FractionDisplay
          className='mb-[2rem]'
          label='Joint Ratio'
          numerator='<strong className="font-medium">Agonist Muscle MVIC</strong> Peak Force'
          denominator='<strong className="font-medium">Antagonist Muscle MVIC</strong> Peak Force'
        />
        <p className='max-w-[14.5rem] self-start rounded-lg bg-[#F8F8F8] p-[1rem_0.5rem] text-left text-[1.2rem] text-[#C2C2C2] md:ml-auto md:text-right'>
          <strong className='font-medium'>MVIC:</strong> Maximal Voluntary Isometric Contraction
        </p>
      </div>

      {/* Shared Unit Dropdown */}
      <UnitDropdown units={units} activeUnitId={unit} onChange={setUnit} />

      {/* Inputs */}
      <ForceInput
        className='mb-[1.4rem]'
        labelLines={['Agonist MVIC:', 'Peak Force']}
        units={units}
        activeUnit={unit}
        value={isMobile ? tempAgonistValue : agonistValue}
        onChange={(e) => {
          if (isMobile) {
            setTempAgonistValue(e.target.value);
          } else {
            setAgonistValue(e.target.value);
          }
        }}
      />
      <ForceInput
        className='mb-[1.4rem]'
        labelLines={['Antagonist MVIC:', 'Peak Force']}
        units={units}
        activeUnit={unit}
        value={isMobile ? tempAntagonistValue : antagonistValue}
        onChange={(e) => {
          if (isMobile) {
            setTempAntagonistValue(e.target.value);
          } else {
            setAntagonistValue(e.target.value);
          }
        }}
      />

      {/* Result with unit */}
      <div className='mb-[3.1rem] flex items-center justify-between font-normal text-primary'>
        <span className='ml-auto text-[1.4rem] font-medium'>Agonist: Antagonist Ratio</span>
        <span className='ml-[2rem] border-l-[0.2rem] border-[#D9D9D9] pl-[2rem] text-[1.8rem] sm:text-[3rem]'>
          {isNaN(ratio) || !isFinite(ratio) ? '0.0' : formattedRatio}
        </span>
      </div>

      <div className='relative flex items-center gap-[1rem]'>
        <div className='absolute inset-x-[-9999px] top-[-2rem] h-[20rem] bg-[#F8F8F8]' />

        <div className='flex w-full items-center gap-[1.6rem] text-[1.2rem]'>
          <Bookmaker className='relative z-10 hidden h-[2rem] w-[1.6rem] max-w-[1.6rem] shrink-0 text-primary md:block' />

          <p className='text-pretty'>
            For more information on joint ratios and their applications, check out these articles on
            the{' '}
            <NextLink
              className='font-light underline'
              href='https://theprehabguys.com/hamstring-to-quadriceps-strength-ratio/'
            >
              knee
            </NextLink>
            ,{' '}
            <NextLink
              className='font-light underline'
              href='https://www.globalperformanceinsights.com/post/measure-shoulder-strength-vald-performance-forceframe-testing#:~:text=What%20is%20the%20ER%3AIR,dominant%20arm%20of%20throwing%20athletes.'
            >
              shoulder
            </NextLink>{' '}
            and{' '}
            <NextLink
              className='font-light underline'
              href='https://www.globalperformanceinsights.com/post/measuring-hip-groin-strength-vald-performance-forceframe-testing#:~:text=Specifically%2C%20the%20adductor%2Dabductor%20(,a%20perfect%20ratio%20of%201.'
            >
              hip
            </NextLink>{' '}
            from partners like Prehab Physical Therapy and Global Performance Insights.
          </p>
        </div>
        {isMobile && (
          <button
            onClick={handleCalculate}
            className='common-btn-transition right-0 bottom-0 relative ml-auto inline-flex h-[3.7rem] max-h-[3.7rem] items-center justify-center rounded-[0.6rem] border-primary bg-primary px-[2rem] py-[0.8rem] text-[1.6rem] font-normal leading-[1.6rem] tracking-[-0.0304rem] text-[#fff]! hover:border-primary--v2 hover:bg-primary--v2 hover:text-primary--v2'
            disabled={!hasPendingChanges}
          >
            Calculate
          </button>
        )}
      </div>
    </CardMetric>
  );
};

export default JointRatioCard;
